/* ═══════════════════════════════════════════════════════════════
   usePagination — the Pages view's measurer (display only).

   After each layout (ResizeObserver on the sheet + the surface's own change
   feed, coalesced to ONE animation frame) it reads the top-level blocks'
   rects fresh from the DOM — never holding element refs across frames —
   runs `paginate`, and exposes where the pages break. It then sizes each
   drawn break so every page on screen is exactly the forma's page height:
   the bottom of a page's text area is filled with blank paper down to its
   footer, on a fixed page grid, so the sheet's page backdrop lines up.

   OFF (the default, Workspace ▸ View ▸ Continuous) → it subscribes to
   nothing, measures nothing, and returns the empty result.

   Hand-built by Josh's express approval (2026-11-02) — see paginate.ts.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { StandardConfig } from '../../../shared/types';
import { inToPx, resolvePageGeometry } from '../../../shared/body-layout';
import {
  EMPTY_PAGINATION,
  blankPageRule,
  paginate,
  type BlockMeasure,
  type PageBlockKind,
  type PageBreak,
  type PaginateResult,
} from './paginate';

/** The class every drawn break carries (PM widget or React element). */
export const PAGE_BREAK_CLASS = 'dsg-pagebreak';

export interface PagerMeasureCtx {
  sheet: HTMLElement;
  /** Screen px per layout px (zoom × UI scale). */
  scale: number;
}

export interface PagerCollect {
  blocks: BlockMeasure[];
  /** Where the renderer anchors each break (a ProseMirror position, or a
   *  block index). Absent → the block index. Null → not drawable. */
  anchorOf?: (brk: PageBreak) => number | null;
}

/** One paged surface: the Designer's ProseMirror document, or Forma
 *  Studio's sample body. */
export interface PagerSurface {
  collect: (ctx: PagerMeasureCtx) => PagerCollect | null;
  /** Content changed in a way the sheet's size may not show. */
  subscribe?: (onChange: () => void) => () => void;
}

export interface Pagination extends PaginateResult {
  anchors: (number | null)[];
}

export const EMPTY_PAGES: Pagination = Object.freeze({
  ...EMPTY_PAGINATION,
  anchors: [],
}) as Pagination;

const px = (n: number) => `${n}px`;

function num(v: string | null | undefined): number {
  const n = parseFloat(v || '');
  return Number.isFinite(n) ? n : 0;
}

/** A paragraph's line pitch in layout px, or undefined when the browser
 *  only knows "normal" (then the paragraph is treated as unsplittable). */
function lineHeightOf(cs: CSSStyleDeclaration): number | undefined {
  const lh = cs.lineHeight;
  if (!lh || lh === 'normal') return undefined;
  if (lh.endsWith('px')) return num(lh) || undefined;
  const k = num(lh);
  return k ? k * num(cs.fontSize) || undefined : undefined;
}

/** Heights of the drawn breaks INSIDE an element (a split paragraph). */
function innerBreakHeight(el: HTMLElement, scale: number): number {
  let h = 0;
  el.querySelectorAll<HTMLElement>(`.${PAGE_BREAK_CLASS}`).forEach((w) => {
    h += w.getBoundingClientRect().height / scale;
  });
  return h;
}

/**
 * Measure a flow of top-level blocks in NATURAL coordinates — as if no
 * page break were drawn. Heights come from the DOM (minus any break drawn
 * inside the block); positions are re-stacked with CSS margin collapsing,
 * so a break drawn between two blocks never feeds back into where they
 * break. Origin = the top of page 1's text area (the sheet body). A tail
 * block (post-body bands, End of Section) follows the flow.
 */
export function measureFlow(
  sheet: HTMLElement,
  flowEl: HTMLElement,
  els: Array<HTMLElement | null>,
  kinds: PageBlockKind[],
  scale: number
): BlockMeasure[] {
  const body = sheet.querySelector<HTMLElement>('.dsg-sheet-body');
  if (!body) return [];
  const bodyRect = body.getBoundingClientRect();
  const out: BlockMeasure[] = [];
  let prevBottom = 0;
  let prevMb = 0;
  els.forEach((el, i) => {
    if (!el) {
      out.push({ id: String(i), top: prevBottom, height: 0, kind: kinds[i] || 'block' });
      return;
    }
    const r = el.getBoundingClientRect();
    const cs = getComputedStyle(el);
    const mt = Math.max(0, num(cs.marginTop));
    const mb = Math.max(0, num(cs.marginBottom));
    const height = Math.max(0, r.height / scale - innerBreakHeight(el, scale));
    const top = i === 0 ? (r.top - bodyRect.top) / scale : prevBottom + Math.max(prevMb, mt);
    const kind = kinds[i] || 'block';
    const m: BlockMeasure = { id: el.dataset.blockId || String(i), top, height, kind };
    if (i > 0) m.spaceBefore = mt;
    if (kind === 'para') {
      const lh = lineHeightOf(cs);
      if (lh) m.lineHeight = lh;
    }
    out.push(m);
    prevBottom = top + height;
    prevMb = mb;
  });
  // The tail: whatever the sheet body draws after the flow (post-body
  // bands, End of Section), minus the last page's blank-paper fill.
  const flowRect = flowEl.getBoundingClientRect();
  const fill = num(getComputedStyle(body).paddingBottom);
  const tailH = (bodyRect.bottom - flowRect.bottom) / scale - fill;
  if (tailH > 1) {
    out.push({
      id: '__tail',
      top: out.length ? prevBottom + prevMb : (flowRect.bottom - bodyRect.top) / scale,
      height: tailH,
      kind: 'tail',
    });
  }
  return out;
}

/**
 * Size every drawn break so each page is exactly one page tall, on a fixed
 * grid: page k spans [(k-1)(P+G), (k-1)(P+G)+P] from the sheet's top. Writes
 * only the breaks' own presentation (fill height, horizontal shift) and the
 * sheet's `--dsg-last-fill` — never the document.
 */
function applyPageFills(
  sheet: HTMLElement,
  scale: number,
  forma: StandardConfig | null,
  result: PaginateResult
) {
  const geo = resolvePageGeometry(forma);
  const P = inToPx(geo.pageHeightIn);
  const mB = inToPx(geo.marginBottomIn);
  const sheetRect = sheet.getBoundingClientRect();
  const widgets = Array.from(sheet.querySelectorAll<HTMLElement>(`.${PAGE_BREAK_CLASS}`));
  const gapEl = sheet.querySelector<HTMLElement>('.dsg-pb-gap');
  const G = gapEl ? gapEl.getBoundingClientRect().height / scale : 0;
  const slot = (k: number) => (k - 1) * (P + G) + P - mB;
  let delta = 0;
  for (const w of widgets) {
    const r = w.getBoundingClientRect();
    // Horizontal: span the whole page whatever block it sits in.
    const left = (r.left - sheetRect.left) / scale;
    if (Math.abs(left) > 0.25) w.style.marginLeft = px(num(w.style.marginLeft) - left);
    // Vertical: blank paper down to this page's footer.
    const fillEl = w.querySelector<HTMLElement>('.dsg-pb-fill');
    if (!fillEl) continue;
    const curH = num(fillEl.style.height);
    const curM = num(w.style.marginTop);
    const top = (r.top - sheetRect.top) / scale + delta - curM;
    let k = Math.max(1, Number(w.dataset.pbPage) || 1);
    let fill = slot(k) - top;
    // A little negative is the previous block's trailing space — pull the
    // break up over it. More means the page overran: next grid page.
    const prev = w.previousElementSibling as HTMLElement | null;
    const room = prev ? Math.max(0, num(getComputedStyle(prev).marginBottom)) : 0;
    while (fill < -room - 0.5 && k < 10000) fill = slot(++k) - top;
    const nextH = Math.max(0, fill);
    const nextM = Math.min(0, fill);
    if (Math.abs(nextH - curH) > 0.25) fillEl.style.height = px(nextH);
    if (Math.abs(nextM - curM) > 0.25) w.style.marginTop = nextM ? px(nextM) : '';
    delta += nextH - curH + (nextM - curM);
  }
  // The last page: blank paper from the end of the text to its footer.
  const body = sheet.querySelector<HTMLElement>('.dsg-sheet-body');
  if (!body) return;
  const cur = num(sheet.style.getPropertyValue('--dsg-last-fill'));
  const end = (body.getBoundingClientRect().bottom - sheetRect.top) / scale + delta - cur;
  let k = Math.max(1, result.numPages - (result.blankPage ? 1 : 0));
  let fill = slot(k) - end;
  while (fill < -0.5 && k < 10000) fill = slot(++k) - end;
  if (Math.abs(fill - cur) > 0.25)
    sheet.style.setProperty('--dsg-last-fill', px(Math.max(0, fill)));
}

export interface UsePaginationOpts {
  enabled: boolean;
  forma: StandardConfig | null;
  sheetRef: React.RefObject<HTMLElement | null>;
  surface: PagerSurface | null;
}

/** The Pages view's state. Off → `EMPTY_PAGES`, no subscriptions. */
export function usePagination({ enabled, forma, sheetRef, surface }: UsePaginationOpts): {
  pages: Pagination;
  schedule: () => void;
} {
  const [pages, setPages] = React.useState<Pagination>(EMPTY_PAGES);
  const formaRef = React.useRef(forma);
  formaRef.current = forma;
  const lastKey = React.useRef('');
  const raf = React.useRef(0);
  const surfaceRef = React.useRef(surface);
  surfaceRef.current = surface;

  const run = React.useCallback(() => {
    raf.current = 0;
    const sheet = sheetRef.current;
    const s = surfaceRef.current;
    if (!sheet || !s || !sheet.isConnected) return;
    const w = sheet.offsetWidth;
    const scale = w ? sheet.getBoundingClientRect().width / w : 1;
    if (!(scale > 0)) return;
    const c = s.collect({ sheet, scale });
    if (!c) return;
    const f = formaRef.current;
    const geo = resolvePageGeometry(f);
    const result = paginate(c.blocks, inToPx(geo.contentHeightIn), {
      widowOrphan: geo.widowOrphan,
      blankPage: blankPageRule(f),
    });
    const anchors = result.breaks.map((b) => (c.anchorOf ? c.anchorOf(b) : b.beforeBlockIndex));
    const key = JSON.stringify([result, anchors]);
    if (key !== lastKey.current) {
      lastKey.current = key;
      setPages({ ...result, anchors });
    }
    applyPageFills(sheet, scale, f, result);
  }, [sheetRef]);

  const schedule = React.useCallback(() => {
    if (raf.current) return;
    raf.current = requestAnimationFrame(run);
  }, [run]);

  // Subscriptions — only while on.
  React.useEffect(() => {
    if (!enabled || !surface) return;
    // A new surface (another document) owes a fresh layout.
    lastKey.current = '';
    const sheet = sheetRef.current;
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    if (sheet && ro) ro.observe(sheet);
    const off = surface.subscribe?.(schedule);
    schedule();
    return () => {
      ro?.disconnect();
      off?.();
      if (raf.current) cancelAnimationFrame(raf.current);
      raf.current = 0;
    };
  }, [enabled, surface, sheetRef, schedule]);

  // A forma edit can restyle without resizing the sheet — re-measure.
  React.useEffect(() => {
    if (enabled) schedule();
  }, [enabled, forma, schedule]);

  // Turned off: forget the last layout and leave the sheet as it was.
  React.useEffect(() => {
    if (enabled) return;
    lastKey.current = '';
    setPages(EMPTY_PAGES);
    sheetRef.current?.style.removeProperty('--dsg-last-fill');
  }, [enabled, sheetRef]);

  // A new layout was drawn (breaks added/removed) — size it this frame.
  React.useEffect(() => {
    if (enabled && pages !== EMPTY_PAGES) schedule();
  }, [enabled, pages, schedule]);

  return { pages: enabled ? pages : EMPTY_PAGES, schedule };
}
